import "@/App.css";
import "@/index.css";
import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Menu, 
  X, 
  Play, 
  ChevronDown, 
  Send, 
  CheckCircle2,
  Sparkles,
  Users,
  Award,
  Zap,
  Globe,
  Calendar,
  Building2,
  PartyPopper,
  Phone
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Design assets from design_guidelines.json
const ASSETS = {
  hero: "https://static.prod-images.emergentagent.com/jobs/ec105bc3-5855-4032-91e6-f4eb0d49821b/images/c1a597ea154d3d5d825791ed7dfd2d663de3aefd05359aae493bc1868b61dd04.png",
  droneCloseup: "https://static.prod-images.emergentagent.com/jobs/ec105bc3-5855-4032-91e6-f4eb0d49821b/images/37451bb3731aea2c42b2b874b99111d5f4a3ab2220cbf0d3326e4868e95b2362.png",
  lightTrails: "https://static.prod-images.emergentagent.com/jobs/ec105bc3-5855-4032-91e6-f4eb0d49821b/images/eff26ab3f02ec889465ee9452591cdcf784ddc1d184580332f8b15f04305b30c.png",
  eventCrowd: "https://images.unsplash.com/photo-1561504221-3ad2c77a1c30?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxuaWdodCUyMGV2ZW50JTIwY3Jvd2QlMjBsb29raW5nJTIwdXB8ZW58MHx8fHwxNzc1OTExMDAzfDA&ixlib=rb-4.1.0&q=85",
};

// YouTube video IDs for drone shows
const YOUTUBE_VIDEOS = [
  { id: "1O0BxM9AtHY", title: "Spectacular Drone Light Show" },
];

// Animated SVG Drone Component
const AnimatedDrone = ({ className, delay = 0 }) => (
  <motion.div
    className={`absolute ${className}`}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay, duration: 1 }}
  >
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="animate-pulse-glow">
      <circle cx="12" cy="12" r="3" fill="#007AFF" />
      <circle cx="12" cy="12" r="6" fill="#007AFF" fillOpacity="0.3" />
      <circle cx="12" cy="12" r="10" fill="#007AFF" fillOpacity="0.1" />
    </svg>
  </motion.div>
);

// Floating particles background
const DroneParticles = () => {
  const particles = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 3 + 1,
    delay: Math.random() * 5,
    duration: Math.random() * 3 + 4,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full bg-primary"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
          }}
          animate={{
            opacity: [0.2, 1, 0.2],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#tarifs", label: "Tarifs" },
    { href: "#services", label: "Services" },
    { href: "#spectacles", label: "Spectacles" },
    { href: "#about", label: "À propos" },
    { href: "#contact", label: "Contact" },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "glass border-b border-white/10"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a
              href="#"
              data-testid="logo-link"
              className="flex items-center gap-3"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold tracking-tight">
                drones3x<span className="text-primary">.ch</span>
              </span>
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`nav-${link.href.slice(1)}`}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-sm text-gray-400 hover:text-white transition-colors duration-300"
                >
                  {link.label}
                </a>
              ))}
              <Button
                data-testid="nav-cta-btn"
                onClick={(e) => scrollToSection(e, "#contact")}
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-6"
              >
                Demander un devis
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              className="md:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "tween", duration: 0.3 }}
            className="fixed inset-0 z-40 glass md:hidden"
          >
            <div className="flex flex-col items-center justify-center h-full gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="text-2xl font-heading font-light text-white hover:text-primary transition-colors"
                >
                  {link.label}
                </a>
              ))}
              <Button
                onClick={(e) => scrollToSection(e, "#contact")}
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-lg mt-4"
              >
                Demander un devis
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <motion.div
        style={{ y }}
        className="absolute inset-0"
      >
        <img
          src={ASSETS.hero}
          alt="Spectacle de drones lumineux au-dessus d'un lac en Suisse - drones3x.ch"
          className="w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
      </motion.div>

      {/* Animated Drones */}
      <DroneParticles />
      <AnimatedDrone className="top-1/4 left-1/4 animate-drone-1" delay={0} />
      <AnimatedDrone className="top-1/3 right-1/3 animate-drone-2" delay={0.5} />
      <AnimatedDrone className="bottom-1/3 left-1/3 animate-drone-1" delay={1} />

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span className="inline-block text-xs tracking-[0.2em] uppercase font-bold text-primary mb-6">
            Spectacles de drones en Suisse
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="font-heading text-5xl sm:text-6xl lg:text-[5rem] font-light tracking-tighter leading-none mb-8"
        >
          <span className="block">Illuminez le ciel</span>
          <span className="block gradient-text">avec nos drones</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="text-base sm:text-lg leading-relaxed text-gray-400 max-w-2xl mx-auto mb-12"
        >
          Créez des moments inoubliables avec nos spectacles de drones synchronisés.
          Des chorégraphies lumineuses spectaculaires pour vos événements les plus prestigieux.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Button
            data-testid="hero-cta-btn"
            onClick={scrollToContact}
            className="btn-primary text-white rounded-full px-8 py-6 text-lg glow-blue"
          >
            Planifier votre spectacle
          </Button>
          <Button
            data-testid="hero-secondary-btn"
            variant="outline"
            onClick={() => document.querySelector("#spectacles")?.scrollIntoView({ behavior: "smooth" })}
            className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-full px-8 py-6 text-lg"
          >
            <Play className="w-5 h-5 mr-2" />
            Voir nos spectacles
          </Button>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center gap-2 text-gray-500"
        >
          <span className="text-xs tracking-wider uppercase">Défiler</span>
          <ChevronDown className="w-5 h-5" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const stats = [
    { value: "500+", label: "Spectacles réalisés", icon: Sparkles },
    { value: "50K+", label: "Spectateurs émerveillés", icon: Users },
    { value: "100%", label: "Satisfaction client", icon: Award },
    { value: "24/7", label: "Support disponible", icon: Zap },
  ];

  return (
    <section className="relative py-20 border-y border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="text-center"
            >
              <stat.icon className="w-8 h-8 text-primary mx-auto mb-4" />
              <div className="font-heading text-4xl md:text-5xl font-light mb-2 stat-number">
                {stat.value}
              </div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Wedding Promo Section
const WeddingPromoSection = () => {
  return (
    <section data-testid="wedding-promo-section" className="relative py-16 md:py-20 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-transparent to-accent/10" />
      
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-3xl p-8 md:p-12 border border-primary/30 relative overflow-hidden"
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-accent/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative grid md:grid-cols-3 gap-8 items-center">
            {/* Left content */}
            <div className="md:col-span-2">
              <span className="inline-block text-xs tracking-[0.2em] uppercase font-bold text-accent mb-4">
                Offre Mariage
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-4">
                Illuminez votre<br />
                <span className="gradient-text">plus beau jour</span>
              </h2>
              <p className="text-base sm:text-lg leading-relaxed text-gray-400 mb-6">
                Offrez à vos invités un spectacle de drones inoubliable. 
                Nos chorégraphies personnalisées créent des moments magiques 
                avec vos initiales, cœurs et formes romantiques dans le ciel.
              </p>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-gray-300">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <span>Chorégraphie personnalisée (initiales, date, cœurs)</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <span>50 à 200 drones synchronisés</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <span>Spectacle de 10 à 15 minutes</span>
                </li>
                <li className="flex items-center gap-3 text-gray-300">
                  <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                  <span>Équipe technique sur place</span>
                </li>
              </ul>
              
              {/* Price & CTA inline */}
              <div className="flex flex-wrap items-center gap-6">
                <div>
                  <span className="text-sm text-gray-400">À partir de</span>
                  <div className="font-heading text-4xl font-light text-white">
                    8'000 <span className="text-xl text-gray-400">CHF</span>
                  </div>
                </div>
                <div className="flex flex-col items-start gap-2">
                  <a
                    href="tel:+33666961535"
                    data-testid="wedding-call-btn"
                    className="flex items-center justify-center gap-3 bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-105 hover:shadow-[0_0_30px_rgba(0,122,255,0.5)]"
                  >
                    <Phone className="w-5 h-5" />
                    Appeler maintenant
                  </a>
                  <div className="text-sm">
                    <span className="text-white font-medium">Anna</span>
                    <span className="text-gray-500 ml-2">· Basée sur Paris</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right content - Video */}
            <div className="flex justify-center">
              <div className="relative w-full max-w-[280px] aspect-[9/16] rounded-2xl overflow-hidden glow-blue">
                <iframe
                  data-testid="wedding-video"
                  src="https://www.youtube.com/embed/Bo0rIKtGPeg?rel=0&modestbranding=1"
                  title="Spectacle de drones mariage"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const packages = [
    {
      drones: "100",
      price: "8'000",
      originalPrice: "11'500",
      discount: "30%",
      popular: false,
    },
    {
      drones: "200",
      price: "12'000",
      originalPrice: "17'000",
      discount: "30%",
      popular: true,
    },
    {
      drones: "500",
      price: "45'000",
      originalPrice: "65'000",
      discount: "30%",
      popular: false,
    },
  ];

  const features = [
    "Durée : 14 minutes",
    "12 tableaux personnalisés",
    "Chorégraphie sur mesure",
    "Bande son personnalisée",
    "Équipe technique sur place",
    "Assurance incluse",
  ];

  return (
    <section id="tarifs" data-testid="pricing-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-primary mb-4 block">
            Nos tarifs
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-4">
            Les tarifs les plus optimisés du marché
          </h2>
          <p className="text-base sm:text-lg leading-relaxed text-gray-400 max-w-2xl mx-auto">
            Tous nos spectacles sont entièrement conçus sur mesure afin de s'adapter 
            parfaitement à votre événement, votre message et votre identité visuelle.
          </p>
        </motion.div>

        {/* Info badges */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          <div className="glass rounded-full px-6 py-3 text-sm">
            <span className="text-primary font-medium">Durée :</span> 14 min
          </div>
          <div className="glass rounded-full px-6 py-3 text-sm">
            <span className="text-primary font-medium">12</span> Tableaux
          </div>
          <div className="glass rounded-full px-6 py-3 text-sm">
            <span className="text-primary font-medium">Préparation :</span> 8 semaines
          </div>
        </div>

        {/* Pricing cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {packages.map((pkg, i) => (
            <motion.div
              key={pkg.drones}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`relative glass rounded-2xl p-8 card-hover ${
                pkg.popular ? 'border-primary/50 ring-1 ring-primary/30' : 'border-white/10'
              }`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-primary text-white text-xs font-bold px-4 py-1 rounded-full">
                    POPULAIRE
                  </span>
                </div>
              )}
              
              {/* Discount badge */}
              <div className="absolute top-4 right-4">
                <span className="bg-accent/20 text-accent text-xs font-bold px-3 py-1 rounded-full">
                  -{pkg.discount}
                </span>
              </div>

              <div className="text-center mb-6">
                <div className="font-heading text-5xl font-light text-white mb-2">
                  {pkg.drones}
                </div>
                <div className="text-gray-400">drones</div>
              </div>

              <div className="text-center mb-6">
                <div className="text-gray-500 line-through text-sm mb-1">
                  {pkg.originalPrice} CHF
                </div>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-sm text-gray-400">À partir de</span>
                </div>
                <div className="font-heading text-4xl font-light text-white">
                  {pkg.price} <span className="text-xl text-gray-400">CHF</span>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {features.map((feature) => (
                  <li key={feature} className="flex items-center gap-3 text-sm text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <a
                href="tel:+33666961535"
                data-testid={`pricing-cta-${pkg.drones}`}
                className={`flex items-center justify-center gap-2 w-full rounded-full px-6 py-4 font-medium transition-all hover:scale-105 ${
                  pkg.popular
                    ? 'bg-primary hover:bg-primary/90 text-white hover:shadow-[0_0_30px_rgba(0,122,255,0.5)]'
                    : 'bg-white/5 hover:bg-white/10 border border-white/10 text-white'
                }`}
              >
                <Phone className="w-4 h-4" />
                Contactez-nous
              </a>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <p className="text-gray-400 mb-4">
            Besoin d'un spectacle avec plus de 500 drones ?
          </p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 text-primary hover:text-accent transition-colors"
          >
            Demandez un devis personnalisé
            <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Services Section (Bento Grid)
const ServicesSection = () => {
  const services = [
    {
      icon: PartyPopper,
      title: "Événements privés",
      description: "Mariages, anniversaires, fêtes - créez un moment magique avec un spectacle personnalisé.",
      span: "md:col-span-8",
      featured: true,
    },
    {
      icon: Building2,
      title: "Corporate",
      description: "Lancements de produits et événements d'entreprise.",
      span: "md:col-span-4",
    },
    {
      icon: Globe,
      title: "Festivals",
      description: "Spectacles grand format pour festivals et événements publics.",
      span: "md:col-span-4",
    },
    {
      icon: Calendar,
      title: "Événements sportifs",
      description: "Animations spectaculaires pour compétitions et cérémonies sportives.",
      span: "md:col-span-8",
      featured: true,
    },
  ];

  return (
    <section id="services" data-testid="services-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-primary mb-4 block">
            Nos services
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">
            Des spectacles pour chaque occasion
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {services.map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`${service.span} glass rounded-2xl p-8 card-hover bento-item`}
            >
              <service.icon className="w-10 h-10 text-primary mb-6" />
              <h3 className="font-heading text-xl sm:text-2xl font-medium mb-3">
                {service.title}
              </h3>
              <p className="text-base leading-relaxed text-gray-400">
                {service.description}
              </p>
              {service.featured && (
                <div
                  className="absolute inset-0 rounded-2xl pointer-events-none"
                  style={{
                    background: "url(" + ASSETS.lightTrails + ")",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    opacity: 0.1,
                  }}
                />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Video Section
const VideoSection = () => {
  return (
    <section id="spectacles" data-testid="video-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-primary mb-4 block">
            Nos réalisations
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight">
            Découvrez nos spectacles
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto"
        >
          <div className="video-glow rounded-2xl overflow-hidden">
            <div className="video-container">
              <iframe
                data-testid="youtube-video"
                src={`https://www.youtube.com/embed/${YOUTUBE_VIDEOS[0].id}?rel=0&modestbranding=1`}
                title={YOUTUBE_VIDEOS[0].title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" data-testid="about-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-primary mb-4 block">
              À propos
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-8">
              Pionniers du spectacle aérien en Suisse
            </h2>
            <p className="text-base sm:text-lg leading-relaxed text-gray-400 mb-6">
              Depuis notre création, nous repoussons les limites du possible avec nos spectacles
              de drones innovants. Notre équipe d'experts combine technologie de pointe et
              créativité artistique pour créer des expériences visuelles uniques.
            </p>
            <p className="text-base sm:text-lg leading-relaxed text-gray-400 mb-8">
              Chaque spectacle est conçu sur mesure, synchronisé avec précision et orchestré
              pour créer des moments de pure magie dans le ciel nocturne.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="glass rounded-xl px-6 py-4">
                <div className="font-heading text-2xl font-light text-primary">100+</div>
                <div className="text-sm text-gray-400">Drones par spectacle</div>
              </div>
              <div className="glass rounded-xl px-6 py-4">
                <div className="font-heading text-2xl font-light text-primary">GPS</div>
                <div className="text-sm text-gray-400">Précision millimétrique</div>
              </div>
              <div className="glass rounded-xl px-6 py-4">
                <div className="font-heading text-2xl font-light text-primary">LED</div>
                <div className="text-sm text-gray-400">16M de couleurs</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden glow-blue">
              <img
                src={ASSETS.droneCloseup}
                alt="Drone professionnel haute technologie pour spectacles aériens en Suisse"
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    event_type: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, event_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData, {
        timeout: 30000, // 30 seconds timeout
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.data && response.data.id) {
        setIsSuccess(true);
        toast.success("Message envoyé avec succès !");
        setFormData({
          name: "",
          email: "",
          phone: "",
          company: "",
          event_type: "",
          message: "",
        });
        setTimeout(() => setIsSuccess(false), 3000);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 md:py-32"
      style={{
        backgroundImage: `url(${ASSETS.lightTrails})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 bg-background/90" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-primary mb-4 block">
              Contact
            </span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-8">
              Créons ensemble votre spectacle
            </h2>
            <p className="text-base sm:text-lg leading-relaxed text-gray-400 mb-8">
              Vous avez un événement en tête ? Contactez-nous pour discuter de votre projet
              et recevoir un devis personnalisé.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Phone className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <div className="text-sm text-gray-400">Téléphone</div>
                  <a href="tel:+33666961535" className="text-white hover:text-primary transition-colors">+33 6 66 96 15 35</a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Globe className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <div className="text-sm text-gray-400">Site web</div>
                  <div className="text-white">drones3x.ch</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <div className="text-sm text-gray-400">Disponibilité</div>
                  <div className="text-white">Toute l'année en Suisse</div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="glass rounded-2xl p-8 space-y-6"
            >
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-400">Nom *</Label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="contact-name-input"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input bg-transparent border-white/20 focus:border-primary rounded-lg"
                    placeholder="Votre nom"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-400">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="contact-email-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input bg-transparent border-white/20 focus:border-primary rounded-lg"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-400">Téléphone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    data-testid="contact-phone-input"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input bg-transparent border-white/20 focus:border-primary rounded-lg"
                    placeholder="+41 XX XXX XX XX"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-gray-400">Entreprise</Label>
                  <Input
                    id="company"
                    name="company"
                    data-testid="contact-company-input"
                    value={formData.company}
                    onChange={handleChange}
                    className="form-input bg-transparent border-white/20 focus:border-primary rounded-lg"
                    placeholder="Nom de l'entreprise"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-400">Type d'événement</Label>
                <Select onValueChange={handleSelectChange} value={formData.event_type}>
                  <SelectTrigger
                    data-testid="contact-event-type-select"
                    className="bg-transparent border-white/20 focus:border-primary rounded-lg text-white"
                  >
                    <SelectValue placeholder="Sélectionnez le type d'événement" />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/10">
                    <SelectItem value="mariage">Mariage</SelectItem>
                    <SelectItem value="corporate">Événement corporate</SelectItem>
                    <SelectItem value="festival">Festival</SelectItem>
                    <SelectItem value="sport">Événement sportif</SelectItem>
                    <SelectItem value="prive">Fête privée</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-gray-400">Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  data-testid="contact-message-input"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="form-input bg-transparent border-white/20 focus:border-primary rounded-lg resize-none"
                  placeholder="Décrivez votre projet..."
                />
              </div>

              <Button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={isSubmitting}
                className="w-full btn-primary text-white rounded-full py-6 text-lg"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <motion.span
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="inline-block"
                    >
                      ⟳
                    </motion.span>
                    Envoi en cours...
                  </span>
                ) : isSuccess ? (
                  <span className="flex items-center gap-2 success-animation">
                    <CheckCircle2 className="w-5 h-5" />
                    Message envoyé !
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    Envoyer le message
                  </span>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="relative py-12 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading text-xl font-semibold tracking-tight">
              drones3x<span className="text-primary">.ch</span>
            </span>
          </div>

          <div className="flex items-center gap-8 text-sm text-gray-400">
            <a href="#tarifs" className="hover:text-white transition-colors">Tarifs</a>
            <a href="#services" className="hover:text-white transition-colors">Services</a>
            <a href="#spectacles" className="hover:text-white transition-colors">Spectacles</a>
            <a href="#about" className="hover:text-white transition-colors">À propos</a>
            <a href="#contact" className="hover:text-white transition-colors">Contact</a>
          </div>

          <div className="text-sm text-gray-500">
            © {currentYear} drones3x.ch - Tous droits réservés
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="min-h-screen bg-background">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#0A0B14',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#FFFFFF',
          },
        }}
      />
      <Navbar />
      <main>
        <HeroSection />
        <StatsSection />
        <WeddingPromoSection />
        <PricingSection />
        <ServicesSection />
        <VideoSection />
        <AboutSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
